import React, { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  Typography,
} from "@mui/material";
import { FormattedMessage } from "react-intl";

import { InputField } from "@/components/InputField";
import DropDownSelect from "@/components/DropDownSelect";
import Modal from "@/components/Modal";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";

import style from "./index.module.css";

import { IDropDownSelectionType } from "@/models/doctor";
import { IProfileInput } from "@/models/patient";

import useIsMobile from "@/utils/hooks/useIsMobile";
import useIsTablet from "@/utils/hooks/useIsTablet";

interface IProps {
  isLoading?: boolean;
  isOpen?: boolean;
  closeHandler?: () => void;
  title: string;
  name: string;
  profileDetailHandler: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    type: IProfileInput
  ) => void;
  age: string;
  mobileNo?: string;
  openMobileChange?: () => void;
  gender: string;
  isDefault?: boolean;
  isGenderOpen: boolean;
  openCloseGenderModal: () => void;
  saveGender: (value: string, type: IDropDownSelectionType) => void;
  saveProfile: () => void;
}

const AddEditProfile: React.FC<IProps> = (props) => {
  const {
    isLoading = false,
    isOpen = true,
    closeHandler,
    title,
    name,
    profileDetailHandler,
    age,
    mobileNo,
    openMobileChange,
    gender,
    isGenderOpen,
    openCloseGenderModal,
    saveGender,
    saveProfile,
    isDefault,
  } = props;

  const [isDefaultProfile, setIsDefaultProfile] = useState<boolean>(false);

  const { isMobile } = useIsMobile();
  const { isTablet } = useIsTablet();

  const handleMainProfileCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsDefaultProfile(isDefault ?? event.target.checked);
    profileDetailHandler(event, IProfileInput.IS_DEFAULT);
  };
  const addEditProfileScreen = () => (
    <Grid item className={style.profileContainer}>
      <Typography component={"h6"} className={style.heading}>
        {title}
      </Typography>
      <FormControl fullWidth className={style.profileInputField}>
        <InputField
          label="Name"
          variant="outlined"
          placeholder="Name"
          value={name}
          onChange={(event): void =>
            profileDetailHandler(event, IProfileInput.NAME)
          }
        />
      </FormControl>
      <FormControl fullWidth className={style.profileInputField}>
        <InputField
          label="Age"
          variant="outlined"
          placeholder="Age"
          value={age}
          onChange={(event): void =>
            profileDetailHandler(event, IProfileInput.AGE)
          }
          fullWidth
        />
      </FormControl>
      {mobileNo && (
        <FormControl
          variant="outlined"
          className={style.profileInputField}
          fullWidth
        >
          <InputField
            label={"Mobile Number*"}
            value={mobileNo}
            onChange={(event): void =>
              profileDetailHandler(event, IProfileInput.MOBILE_NUMBER)
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component={"p"}
                    className={style.selectorText}
                    onClick={openMobileChange}
                  >
                    <FormattedMessage id="change" defaultMessage="change" />
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </FormControl>
      )}
      <FormControl
        variant="outlined"
        fullWidth
        className={style.profileInputField}
      >
        <InputField
          disabled
          label={"Gender"}
          value={gender}
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Typography
                  component={"p"}
                  className={style.selectorText}
                  onClick={openCloseGenderModal}
                >
                  <FormattedMessage id="select" defaultMessage="Select" />
                </Typography>
              </InputAdornment>
            ),
          }}
        />
      </FormControl>
      <FormControl fullWidth>
        <FormControlLabel
          control={
            <Checkbox
              checked={isDefaultProfile}
              color="warning"
              sx={{ padding: "0rem 0.8rem 0rem 1.6rem" }}
              onChange={handleMainProfileCheckbox}
            />
          }
          label={
            <Typography component={"p"} className={style.createProfile}>
              <FormattedMessage
                id="use_as_main_profile"
                defaultMessage="Use as main profile"
              />
            </Typography>
          }
        />
      </FormControl>
      <Button btnTrigger={saveProfile}>
        <FormattedMessage id="save" defaultMessage="save" />
      </Button>
    </Grid>
  );

  const profileLoader = useMemo(() => {
    return (
      <Stack
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"center"}
        alignItems={"center"}
        height={"20vh"}
        width={"100%"}
      >
        <CircularProgress size={30} />
      </Stack>
    );
  }, []);

  useEffect(() => {
    if (typeof isDefault === "boolean") {
      setIsDefaultProfile(isDefault);
    }
  }, [isDefault]);

  return (
    <>
      {isMobile || isTablet ? (
        <Modal isOpen={isOpen} closeHandler={closeHandler}>
          {isLoading ? profileLoader : <>{addEditProfileScreen()}</>}
        </Modal>
      ) : (
        <Dialog
          fullWidth
          maxWidth="xs"
          isOpen={isOpen}
          closeHandler={closeHandler}
        >
          {isLoading ? profileLoader : <>{addEditProfileScreen()}</>}
        </Dialog>
      )}
      {isGenderOpen && (
        <DropDownSelect
          type={IDropDownSelectionType.GENDER}
          isOpen={isGenderOpen}
          closeHandler={openCloseGenderModal}
          title="gender"
          values={[
            { label: "Male", value: "Male" },
            { label: "Female", value: "Female" },
          ]}
          selectedValue={gender || ""}
          saveSelectedValues={saveGender}
          width={35}
        />
      )}
    </>
  );
};

export default AddEditProfile;
